import type {
  ForecastLineDto,
  ForecastLineReason,
  ForecastOverrideInput,
  ForecastShipmentDto,
  ForecastShipmentInput,
  ForecastShipmentsQuery,
  ShipmentForecastDto,
} from '@/api';

import { formatDate, toNumber } from '../../../lib/format';

/*
 * План из истории на стороне экрана: ключи строк, правки менеджера, галочки и
 * тело создания. Правила расчёта живут на сервере (ShipmentForecaster): экран
 * хранит только правки и галочки, а план пересчитывает запросом. Ключ строки -
 * клиент, товар и номер повтора: при пересчёте он не меняется, поэтому правки и
 * галочки переживают перегруппировку отгрузок.
 */

/** Правки менеджера по ключу строки. */
export type ForecastOverrides = ReadonlyMap<string, ForecastOverrideInput>;

/** Галочки, которые менеджер поставил или снял сам, по ключу строки. */
export type ForecastSelection = ReadonlyMap<string, boolean>;

/** Строка предложения вместе с её клиентом - так её адресуют галочки и правки. */
export interface ForecastEntry {
  partnerId: string;
  line: ForecastLineDto;
}

/** Что можно поправить в повторе товара. */
export type ForecastPatch = Partial<
  Pick<
    ForecastOverrideInput,
    'plannedDate' | 'quantity' | 'nextShipmentIntervalDays' | 'warehouseId'
  >
>;

export const buildLineKey = ({ partnerId, line }: ForecastEntry): string =>
  `${partnerId}:${line.productId}:${String(line.occurrence)}`;

/** Правки, где у повторов из entries поля patch поверх их прежних правок. */
export const applyOverride = (
  overrides: ForecastOverrides,
  entries: ReadonlyArray<ForecastEntry>,
  patch: ForecastPatch,
): ForecastOverrides => {
  const next = new Map(overrides);

  for (const entry of entries) {
    const key = buildLineKey(entry);
    const previous = overrides.get(key) ?? {
      partnerId: entry.partnerId,
      productId: entry.line.productId,
      occurrence: entry.line.occurrence,
    };

    next.set(key, { ...previous, ...patch });
  }

  return next;
};

/** Отмечена ли строка: выбор менеджера, а без него - галочка по умолчанию с сервера. */
export const isEntrySelected = (
  selection: ForecastSelection,
  entry: ForecastEntry,
): boolean => selection.get(buildLineKey(entry)) ?? entry.line.isSelected;

/** Галочки, где строки из entries отмечены или сняты разом. */
export const applySelection = (
  selection: ForecastSelection,
  entries: ReadonlyArray<ForecastEntry>,
  isSelected: boolean,
): ForecastSelection => {
  const next = new Map(selection);
  for (const entry of entries) next.set(buildLineKey(entry), isSelected);

  return next;
};

/** Строки отгрузок с их клиентами - для флажков группы. */
export const listEntries = (
  shipments: ReadonlyArray<ForecastShipmentDto>,
): Array<ForecastEntry> =>
  shipments.flatMap((shipment) =>
    shipment.lines.map((line) => ({ partnerId: shipment.partnerId, line })),
  );

/** Состояние флажка группы: отмечены все строки, ни одной или часть. */
export const resolveGroupState = (
  selection: ForecastSelection,
  entries: ReadonlyArray<ForecastEntry>,
): { isChecked: boolean; isIndeterminate: boolean } => {
  const selected = entries.filter((entry) =>
    isEntrySelected(selection, entry),
  ).length;

  return {
    isChecked: entries.length > 0 && selected === entries.length,
    isIndeterminate: selected > 0 && selected < entries.length,
  };
};

/** Клиенты в порядке ответа сервера, каждый со своими отгрузками. */
export const groupByClient = (
  shipments: ReadonlyArray<ForecastShipmentDto>,
): Array<{
  partnerId: string;
  partnerName: string;
  shipments: Array<ForecastShipmentDto>;
}> => {
  const clients = new Map<
    string,
    {
      partnerId: string;
      partnerName: string;
      shipments: Array<ForecastShipmentDto>;
    }
  >();

  for (const shipment of shipments) {
    const client = clients.get(shipment.partnerId) ?? {
      partnerId: shipment.partnerId,
      partnerName: shipment.partnerName,
      shipments: [],
    };

    client.shipments.push(shipment);
    clients.set(shipment.partnerId, client);
  }

  return [...clients.values()];
};

/** Тело расчёта: выбранные клиенты или все, и все правки целиком. */
export const buildForecastQuery = (
  partnerIds: ReadonlyArray<string>,
  isAllClients: boolean,
  overrides: ForecastOverrides,
): ForecastShipmentsQuery => ({
  partnerIds: isAllClients ? null : [...partnerIds],
  allClients: isAllClients,
  overrides: [...overrides.values()],
});

/** Отгрузки к созданию: только те, где отмечена хотя бы одна строка, и только отмеченные строки. */
export const buildShipmentsToCreate = (
  forecast: ShipmentForecastDto,
  selection: ForecastSelection,
): Array<ForecastShipmentInput> =>
  forecast.shipments.flatMap((shipment) => {
    const lines = shipment.lines.filter((line) =>
      isEntrySelected(selection, { partnerId: shipment.partnerId, line }),
    );

    if (lines.length === 0) return [];

    return [
      {
        partnerId: shipment.partnerId,
        plannedDate: shipment.plannedDate,
        warehouseId: shipment.warehouseId ?? null,
        lines: lines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
          price: line.price ?? null,
          nextShipmentIntervalDays: line.nextShipmentIntervalDays,
        })),
      },
    ];
  });

/** Почему строка без галочки - словами для менеджера. */
export const REASON_LABELS: Record<ForecastLineReason, string> = {
  FewDeliveries: 'мало поставок',
  Lapsed: 'давно не заказывали',
  LongOverdue: 'срок давно прошёл',
};

const formatDeliveries = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return `${count} поставка`;
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return `${count} поставки`;
  return `${count} поставок`;
};

/** Основание строки: сколько поставок, какой ритм, когда последняя. */
export const describeBasis = (line: ForecastLineDto): string => {
  const rhythm = line.isManualInterval
    ? `интервал ${String(line.rhythmDays)} дн. задан вручную`
    : `каждые ~${String(line.rhythmDays)} дн.`;

  return [
    formatDeliveries(toNumber(line.deliveries)),
    rhythm,
    line.lastDeliveryDate
      ? `последняя ${formatDate(line.lastDeliveryDate)}`
      : null,
  ]
    .filter((part) => part !== null)
    .join(', ');
};
